import { useRouter } from 'next/router';
import type { FC } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { ErrorBlock } from '../../components/ErrorBlock';
import type { UseQueryCallbackRefetch } from '../../hooks/useQueryCallback';
import { useQueryCallback } from '../../hooks/useQueryCallback';
import { useRefLatest } from '../../hooks/useRefLatest';
import { ReactComponent as IconHierarchyMultiColor } from '../../icons/icon-hierarchy-multicolor.svg';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectIsBot } from '../../redux/slices/utils-slice';
import {
  addExtraYakliParentMessages,
  selectYakliAncestorMessage,
  selectYakliRootMessage,
  selectYakliTargetMessage,
} from '../../redux/slices/yakli-slice';
import { ScrollSectionsProvider } from '../../services/scrollSections';
import type { QueryType, Website } from '../../types/api';
import { handleError } from '../../utils/common-utils';
import { warnOrThrow } from '../../utils/common-utils2';
import { scrollToAnchor } from '../../utils/dom';
import { isBrowser } from '../../utils/web';
import { MobileThreadFooterEditor } from '../MobileThreadFooterEditor';
import { isNotFoundTopic } from '../thread-utils';
import { ThreadAutoHeight } from '../ThreadAutoHeight';
import type { ThreadContextValues } from '../ThreadContexts/ThreadContext';
import { ThreadContextProvider } from '../ThreadContexts/ThreadContext';
import { MessageEditingProvider } from '../ThreadContexts/ThreadEditingContext';
import { mkMessageDOMId } from '../ThreadMessage/thread-service';
import type { ThreadMessageChildrenInstance } from '../ThreadMessage/ThreadMessageNode';
import { ThreadMessageRoot } from '../ThreadMessage/ThreadMessageRoot';
import { useTopicView } from '../useTopicView';
import { useYakliView } from '../useYakliView';
import { ParentMessageQuery } from './query';
import { WithoutAnswersProvider } from './WithoutAnswersProvider';

const messages = defineMessages({
  comments: { defaultMessage: 'comments', id: 'aPgrgC' },
  createCTA: { defaultMessage: 'Create a new topic', id: 'WkpWWI' },
  title: { defaultMessage: 'Discussion started by {author}', id: 'xFSAWO' },
  previous: {
    defaultMessage: `{count,plural,=0{No additional messages} =1{Show # additional message} =2{Show # additional messages} =3{Show # additional messages} =4{Show # additional messages} =5{Show # additional messages} other{Show 5 additional messages (total: #)}}`,
    id: 'A8hETs',
  },
});

// May be useful once we also find a way to get the `childMap` from the redux state, as calculated by getAncestor.
// const selectYakliTargetAncestor = (state: RootState) => {
//   const messages2 = state.threads.messages;
//   if (!state.threads.yakliTargetMessage || !messages2) {
//     return undefined;
//   }
//   let message = messages2[state.threads.yakliTargetMessage];
//   while (message.parentMessageId && messages2[message.parentMessageId]) {
//     message = messages2[message.parentMessageId];
//   }
//   return message;
// };

interface Props {
  websiteWithDiscussion: Website;
  slug: string;
  messageId: string;
  refetch?: UseQueryCallbackRefetch;
}

export const Discussion: FC<Props> = memo(function Discussion(props) {
  const { websiteWithDiscussion, slug, messageId } = props;
  const { query, push } = useRouter();
  const isBot = useAppSelector(selectIsBot);

  const messageRef = useRef<ThreadMessageChildrenInstance>(null);

  // Hack: scroll to the targeted message when the discussion has finished to load.
  const scrolledToAnchorRef = useRef(false);
  useEffect(() => {
    if (!isBot && isBrowser && messageId && !scrolledToAnchorRef.current) {
      scrolledToAnchorRef.current = true;
      const id = Array.isArray(messageId) ? messageId[0] : messageId;
      document.getElementById(id)?.scrollIntoView();
    }
  }, [isBot, messageId]);

  const yakliRootMessage = useAppSelector(selectYakliRootMessage);
  const yakliAncestorMessage = useAppSelector(selectYakliAncestorMessage);
  const yakliTargetMessage = useAppSelector(selectYakliTargetMessage);

  const yakliRootMessageId = yakliRootMessage?.id;
  const yakliAncestorMessageId = yakliAncestorMessage?.id;

  if (!yakliAncestorMessageId) {
    // Security check, because we had a bug here in the past
    warnOrThrow('[Discussion] No yakliAncestorMessageId, cannot run the ParentMessageQuery');
  }

  const everythingIsLoaded =
    yakliRootMessageId === yakliAncestorMessageId ||
    yakliRootMessageId === yakliAncestorMessage?.parentMessageId;

  const topic = websiteWithDiscussion.findTopic;

  // const targetAuthor = yakliTargetMessage?.author as User;
  // const targetIsMine = useIsMine(targetAuthor);

  const dispatch = useAppDispatch();

  const saveExtraLoadedMessages = useCallback(
    (data: QueryType) => {
      const message = data.website?.findMessage;
      dispatch(addExtraYakliParentMessages(message));
    },
    [dispatch],
  );

  const {
    loading,
    error,
    refetch: fetchCollapsedMessages,
  } = useQueryCallback({
    query: ParentMessageQuery,
    variables: { messageId: yakliAncestorMessageId },
    skipInitialFetch: true,
    notifyOnNetworkStatusChange: true,
    disableRefetchOnVarChange: true,
    onResponse: saveExtraLoadedMessages,
    // « Voir les messages précédents » : ce refetch n'est appelé QUE par le
    // clic (skipInitialFetch + disableRefetchOnVarChange — aucun tir de
    // montage) → geste facturé comme Interaction (B10_05).
    context: { gcInteraction: true },
  });
  const loadingRef = useRefLatest(loading);

  const addLayers = useCallback(() => {
    if (loadingRef.current) return;
    fetchCollapsedMessages();
  }, [fetchCollapsedMessages, loadingRef]);

  // Vue de sujet, comme sur la page topic : cette page (deep-link message
  // `/t/[slug]/d/[messageId]`, et la discussion Yakli) résout elle aussi le
  // thread côté serveur, donc elle comptait une vue — deux, avec la
  // régénération ISR. Elle ne monte PAS `ThreadPageTopicLoaded` : sans ce tir,
  // toute une population d'arrivées (liens de notification, e-mails « on vous
  // a répondu », permaliens partagés) tomberait à zéro à la bascule du flag.
  // Appelé avant le garde ci-dessous pour rester inconditionnel (règle des
  // hooks) ; le hook est no-op tant que l'id est absent.
  useTopicView(topic?.findOrCreateThread?.id);
  // Ouverture de permalien (B7_01) : cette page EST le lien direct, donc elle compte à la
  // fois une vue de sujet (ci-dessus) et un `yakli`. Même contrainte de position que le
  // hook précédent — avant le garde, pour rester inconditionnel (règle des hooks).
  useYakliView(topic?.findOrCreateThread?.id);

  if (!topic?.findOrCreateThread?.id || !topic?.id || !topic?.slug) {
    throw new Error(
      `[Discussion] Cannot render a topic which is missing its ID, thread ID or slug.`,
    );
  }

  // @TODO remove tmp while necessary for api
  const threadContextInitialValue = useMemo<ThreadContextValues>(
    () => ({
      threadId: topic?.findOrCreateThread?.id!,
      pageId: topic?.id!,
      slug: topic?.slug!,
      // isEditing: targetIsMine && query.edit === 'true',
      isAnswering: query.answer === 'true',
    }),
    [query.answer, topic?.findOrCreateThread?.id, topic?.id, topic?.slug],
  );

  useEffect(() => {
    if (yakliRootMessage?.status === 'deleted') {
      // If the root message of the yakli page is deleted, we redirect to the topic page.
      push(`/t/${slug}`);
    }
  }, [yakliRootMessage?.status, slug, push, query]);

  // When the yakli discussion is loaded the first time, it should scroll to the target message.
  useEffect(() => {
    const domId = mkMessageDOMId(messageId as string);
    if (domId) {
      setTimeout(() => {
        scrollToAnchor({
          anchorName: domId,
          options: { behavior: 'smooth' },
        });
      }, 200);
    }
  }, [messageId]);

  if (!topic || isNotFoundTopic(topic) || !yakliAncestorMessage || !yakliRootMessageId)
    return <></>;

  if (!topic?.id) throw new Error('[BUG] Cannot render a topic without ID.');

  if (!yakliAncestorMessage.id) {
    handleError('[bug] Discussion: the ancestor ID is missing');
    return null;
  }

  // ThreadMessageRoot with the `withoutAnswers` prop and ThreadMessageNode below is a special configuration, for yakli links. We want to display the first message of a conversation (the top node in the tree path), then a "... load more" block hiding intermediate messages, then the end of the conversation we have already loaded.
  // The first node is loaded on one side (ThreadMessageRoot), the rest of the conversation on another side (ThreadMessageNode below). When clicking "load more" until everything has been loaded, the first node may be loaded twice. So we dedupe. And the easiest way to keep a clean tree is to hide the initial top node, so we hide the ThreadMessageRoot below (hide prop).
  return (
    <>
      <ErrorBlock error={error} />

      <MessageEditingProvider message={yakliTargetMessage}>
        <ThreadContextProvider initialValues={threadContextInitialValue}>
          <ScrollSectionsProvider>
            {!everythingIsLoaded && (
              <WithoutAnswersProvider>
                <ThreadMessageRoot
                  key={yakliRootMessageId}
                  messageId={yakliRootMessageId}
                  unlimited
                  moreBorders={!everythingIsLoaded ? 'bottom' : undefined}
                />
              </WithoutAnswersProvider>
            )}
            <ThreadAutoHeight waitForInitialHeight>
              {!everythingIsLoaded &&
                (loading ? (
                  <div className="flex items-center justify-center mx-auto py-5">
                    <span className="loader"></span>
                  </div>
                ) : (
                  <div
                    className="flex items-center justify-center mx-auto py-5 space-x-2 text-sm font-bold font-source cursor-pointer hover:text-primary"
                    onClick={loading ? undefined : addLayers}
                  >
                    <IconHierarchyMultiColor />
                    <span>
                      <FormattedMessage
                        {...messages.previous}
                        values={{ count: yakliAncestorMessage.position - 1 }}
                      />
                    </span>
                  </div>
                ))}
              <ThreadMessageRoot
                key={yakliAncestorMessage?.id}
                ref={messageRef}
                messageId={yakliAncestorMessage.id}
                targetedId={messageId as string}
                unlimited
                moreBorders={!everythingIsLoaded ? 'top' : undefined}
                isYakliDisplay
              />
            </ThreadAutoHeight>

            <MobileThreadFooterEditor slug={topic?.slug} topicId={topic.id} />
          </ScrollSectionsProvider>
        </ThreadContextProvider>
      </MessageEditingProvider>
    </>
  );
});
